import { combineReducers } from 'redux';
import createFilter from 'redux-persist-transform-filter';
import reduxLocalStorage from 'redux-persist/lib/storage';
import reduxSessionStorage from 'redux-persist/lib/storage/session';
import { persistReducer } from 'redux-persist';

import {
  checkDuplicatedReducerConfigs,
  placeReducerAtFirstPosition,
  checkRepeatedConditions,
  checkRequiredFields,
  removeDuplicatedDataFromArray,
  hasTwoTruthyConditions,
  STORAGE,
} from './utils';

export const createLoadFilter = (reducer, reducerConfig, innerData) => {
  reducerConfig.whitelist = innerData;
  return createFilter(reducer, null, innerData);
};

export const createSaveFilter = ({
  config,
  projectId,
  reducerConfig,
  innerData,
}) => {
  const { reducer, storage, filters } = config;

  const hasSaveAction = filters.some(
    filter => filter.condition && filter.action === 'save',
  );
  const hasLoadAction = filters.some(
    filter => filter.condition && filter.action === 'load',
  );

  if ((!hasSaveAction && hasLoadAction) || (hasSaveAction && !hasLoadAction)) {
    STORAGE[storage].removeItem(`persist:${projectId}:${reducer}`);
  }

  reducerConfig.whitelist = innerData;
  return createFilter(reducer, innerData);
};

export const createPersistFilter = (reducerConfig, innerData) => {
  reducerConfig.whitelist = innerData;
};

export const reducerFiltersFactory = ({
  config,
  projectId,
  reducer,
  reducerConfig,
  filter,
  configs,
}) => {
  const { storage } = config;
  const { data, action } = filter;

  let filterToApply;

  if (action === 'load') {
    const exists = Boolean(
      STORAGE[storage].getItem(`persist:${projectId}:${reducer}`),
    );
    if (exists) filterToApply = createLoadFilter(reducer, reducerConfig, data);
    else if (reducer === 'root' && !hasTwoTruthyConditions(configs)) {
      delete reducerConfig.key;
    }
  }

  if (action === 'save') {
    filterToApply = createSaveFilter({
      config,
      projectId,
      reducerConfig,
      innerData: data,
    });
  }

  if (action === 'persist') createPersistFilter(reducerConfig, data);
  if (action === 'exclude') reducerConfig.blacklist = data;

  if (filterToApply) {
    if (!reducerConfig.transforms) {
      reducerConfig.transforms = [];
    }
    reducerConfig.transforms.push(filterToApply);
  }

  removeDuplicatedDataFromArray(reducerConfig.whitelist);
  removeDuplicatedDataFromArray(reducerConfig.blacklist);
};

export const persistedRootReducer = (
  rootConfig,
  reducers,
  hasPersistedReducers,
) => {
  reducers = combineReducers(reducers);
  let rootReducers;

  if (rootConfig.key) {
    rootConfig.whitelist = removeDuplicatedDataFromArray(rootConfig.whitelist);
    rootConfig.blacklist = removeDuplicatedDataFromArray(rootConfig.blacklist);
    rootReducers = persistReducer(rootConfig, reducers);
    return [rootReducers, true];
  }

  rootReducers = reducers;
  if (hasPersistedReducers) return [rootReducers, true];

  return [rootReducers, false];
};

export const executePersistConfigurations = (
  reducers,
  projectPersistConfigs,
) => {
  const { configs } = projectPersistConfigs;
  const { projectId } = projectPersistConfigs;

  const rootConfig = {};
  let hasPersistedReducers = false;

  checkDuplicatedReducerConfigs(configs);
  placeReducerAtFirstPosition(configs, 'root');

  configs.forEach((config) => {
    const { reducer, storage, filters } = config;
    checkRepeatedConditions(filters);
    checkRequiredFields(
      config,
      ['reducer', 'storage', 'filters'],
      'para poder realizar una configuración de persistencia.',
    );

    filters.forEach((filter) => {
      const { data, action, condition } = filter;
      checkRequiredFields(
        filter,
        ['action', 'condition'],
        'en el filtro para poder realizar una configuración de persistencia.',
      );

      if (reducer === 'root' && condition === true) {
        if (data) {
          throw new Error(
            'No se puede aplicar una configuración de filtros para los reducers configurando el root.'
              + 'Para ello, elimina el reducer del filtro y crea una configuración específica para él.',
          );
        }

        rootConfig.key = `${projectId}:${reducer}`;
        rootConfig.storage = storage === 'session' ? reduxSessionStorage : reduxLocalStorage;

        if (!data) {
          if (action === 'exclude') {
            throw new Error(
              'No se puede excluir todos los reducers cuando se configura el root. Para ello, elimina la configuración.',
            );
          }

          reducerFiltersFactory({
            config,
            projectId,
            reducer,
            reducerConfig: rootConfig,
            filter,
            configs,
          });

          const reducersKeys = Object.keys(reducers).filter(
            reducerKey => reducerKey !== 'router',
          );
          rootConfig.whitelist = reducersKeys;
        }
      }

      if (reducer !== 'root' && condition === true) {
        const reducerConfig = {};
        reducerConfig.key = `${projectId}:${reducer}`;
        reducerConfig.storage = storage === 'session' ? reduxSessionStorage : reduxLocalStorage;

        if (data && Array.isArray(data) && data.length) {
          reducerFiltersFactory({
            config,
            projectId,
            reducer,
            reducerConfig,
            filter,
          });
        }

        if (!data) {
          if (action === 'save') {
            STORAGE[storage].removeItem(`persist:${projectId}:${reducer}`);
            if (!rootConfig.whitelist) rootConfig.whitelist = [];
            rootConfig.whitelist.push(reducer);
          }

          if (action === 'exclude' && rootConfig.whitelist) {
            rootConfig.whitelist = rootConfig.whitelist.filter(
              reducerToCompare => reducer !== reducerToCompare,
            );
          }
        }

        reducers[reducer] = persistReducer(reducerConfig, reducers[reducer]);
        hasPersistedReducers = true;
      }
    });
  });

  return persistedRootReducer(rootConfig, reducers, hasPersistedReducers);
};
